import Vue from 'vue'
import configs from '@/config'

let resource = null
const accountRemovalPath = `${configs.baseUrl}/api/admin/account-removal-requests`

const actions = {
  listRemovalRequests: {
    method: 'GET',
    url: `${accountRemovalPath}`,
  },
  createRemovalRequests: {
    method: 'POST',
    url: `${accountRemovalPath}`,
  },
}

const initResource = () => {
  resource = Vue.resource('', {}, actions)
}

// Mirrors OUTSTANDING_STATUSES in the backend service: a request is outstanding
// while it is queued or already being acted on. 'completed' and 'failed' are
// terminal, so a user is legitimately requestable again after either. The list
// filters by userId but not by status, so the caller does this part.
export const OUTSTANDING_REMOVAL_STATUSES = ['pending', 'processing']

// The list is paged server-side -- limit defaults to 50 and caps at 200 -- so the
// envelope carries the page alongside the row count. Returning the bare array
// would silently cap the table at the newest 50 rows with nothing to page with.
//
// Only the keys the caller actually set are sent: vue-resource serialises an
// undefined value as the literal string "undefined", which the query validator
// rejects with a 400.
//
// `userId` narrows the list to one user's rows, newest first, at every status.
//
// `include` is a comma-separated subset of `user,creator`: `user` enriches each
// row with the removal target's { id, email, firstName, lastName, avatar },
// `creator` does the same for the admin who filed the request. Either is `null`
// when that account is gone.
//
// response: { removalRequests: [{ id, userId, status, createdBy, createdAt, updatedAt, user, creator }], total, limit, offset }
const listRemovalRequests = async ({
  limit, offset, include, userId,
} = {}) => {
  const params = {}
  if (limit !== undefined) params.limit = limit
  if (offset !== undefined) params.offset = offset
  if (include !== undefined) params.include = include
  if (userId !== undefined) params.userId = userId

  const res = await resource.listRemovalRequests(params)
  const body = res.body || {}
  return { removalRequests: body.removalRequests || [], total: body.total || 0 }
}

// payload: { userIds: [...] } and/or { emails: [...] }, at most 50 users in total;
// server sets createdBy from auth.
//
// A batch settles per id rather than rejecting as a whole, so a partial failure
// still answers 200 with the breakdown below. A batch that recorded nothing and
// failed something answers 500 carrying that same body; a payload rejected up
// front (unknown account, over the cap) answers 400 { message }.
//
// response: { created: [{ id, userId, status, createdBy, createdAt, updatedAt }], alreadyPending: [userId, ...], failed: [{ userId, reason }] }
const createRemovalRequests = async (payload) => {
  const res = await resource.createRemovalRequests(payload)
  return res.body
}

export default {
  initResource,
  listRemovalRequests,
  createRemovalRequests,
}
